import { type FC, type MouseEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Trash2 } from 'lucide-react';
import type { VisitStepFormData } from '../../types/form-data.types';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { VisitorAvatar } from 'components/shared/visitor-avatar/visitor-avatar';

interface CurrentVisitorsProps {
  savedData: VisitStepFormData;
  onRemoveVisitor: (index: number) => void;
  isEditMode?: boolean;
}

/**
 * Format phone number using international format based on locale
 */
const formatPhone = (phone: string): string => {
  // formatPhoneNumberIntl expects E164 format (e.g., +12025551234)
  // If phone doesn't start with +, try adding it
  const phoneToFormat = phone.startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}`;
  const formatted = formatPhoneNumberIntl(phoneToFormat as Parameters<typeof formatPhoneNumberIntl>[0]);
  return formatted || phone; // Return original if formatting fails
};

export const CurrentVisitors: FC<CurrentVisitorsProps> = ({ savedData, onRemoveVisitor, isEditMode }) => {
  const { formatMessage } = useIntl();

  const handleRemoveClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      const index = Number(e.currentTarget.dataset.index);
      onRemoveVisitor(index);
    },
    [onRemoveVisitor],
  );

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">
        {formatMessage({
          id: 'createVisitFormV2.visitStep.currentVisitors',
          defaultMessage: 'Current visitors',
        })}
      </h4>
      <div className="space-y-2">
        {savedData.visitors.map((visitor, index) => (
          <div
            key={`${visitor.email || index}`}
            className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border"
          >
            {/* Left side: columns (photo/initials, details, reason) */}
            <div className="flex items-start gap-4">
              {/* Column 1: Photo or Initials */}
              <VisitorAvatar
                firstName={visitor.firstName}
                lastName={visitor.lastName}
                photoUuid={visitor.photoUuid}
                size="md"
              />
              {/* Column 2 & 3: Details and reason (stacked on mobile, side by side on desktop) */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                {/* Column 2: Name, email, phone, company */}
                <div className="min-w-0 sm:pr-4 sm:border-r sm:border-gray-200">
                  <p className="text-sm font-medium text-gray-900">
                    {visitor.firstName} {visitor.lastName}
                  </p>
                  {visitor.email && <p className="text-xs text-gray-500">{visitor.email}</p>}
                  {visitor.phone && <p className="text-xs text-gray-500">{formatPhone(visitor.phone)}</p>}
                  {visitor.companyName && <p className="text-xs text-gray-400">{visitor.companyName}</p>}
                </div>
                {/* Column 3: Reason for visit */}
                {savedData.visitSummary && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      {formatMessage({
                        id: 'createVisitFormV2.visitStep.reasonForVisit',
                        defaultMessage: 'Reason for visit',
                      })}
                    </p>
                    <p className="text-sm text-gray-700 sm:max-w-[200px]">{savedData.visitSummary}</p>
                  </div>
                )}
              </div>
            </div>
            {/* Right side: Trash icon (hidden in edit mode) */}
            {!isEditMode && (
              <button
                type="button"
                data-index={index}
                onClick={handleRemoveClick}
                className="flex-shrink-0 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                aria-label={formatMessage({
                  id: 'createVisitFormV2.visitStep.removeVisitor',
                  defaultMessage: 'Remove visitor',
                })}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
